// ===================================================================
// SMART CITIES HUB - ENUM CONSTANTS
// ===================================================================

export const USER_ROLES = {
  CITY_OFFICIAL: 'city_official',
  STUDENT: 'student',
  PCF_ADMIN: 'pcf_admin'
};

export const USER_ROLE_LABELS = {
  [USER_ROLES?.CITY_OFFICIAL]: 'City Official',
  [USER_ROLES?.STUDENT]: 'Student',
  [USER_ROLES?.PCF_ADMIN]: 'PCF Admin'
};

export const BUSINESS_CATEGORIES = {
  TECHNOLOGY: 'technology',
  TRANSPORTATION: 'transportation',
  ENERGY: 'energy',
  WATER_MANAGEMENT: 'water_management',
  WASTE_MANAGEMENT: 'waste_management',
  GOVERNANCE: 'governance',
  EDUCATION: 'education',
  HEALTHCARE: 'healthcare',
  SAFETY_SECURITY: 'safety_security',
  ECONOMIC_DEVELOPMENT: 'economic_development'
};

export const BUSINESS_CATEGORY_LABELS = {
  [BUSINESS_CATEGORIES?.TECHNOLOGY]: 'Technology',
  [BUSINESS_CATEGORIES?.TRANSPORTATION]: 'Transportation',
  [BUSINESS_CATEGORIES?.ENERGY]: 'Energy',
  [BUSINESS_CATEGORIES?.WATER_MANAGEMENT]: 'Water Management',
  [BUSINESS_CATEGORIES?.WASTE_MANAGEMENT]: 'Waste Management',
  [BUSINESS_CATEGORIES?.GOVERNANCE]: 'Governance',
  [BUSINESS_CATEGORIES?.EDUCATION]: 'Education',
  [BUSINESS_CATEGORIES?.HEALTHCARE]: 'Healthcare',
  [BUSINESS_CATEGORIES?.SAFETY_SECURITY]: 'Safety & Security',
  [BUSINESS_CATEGORIES?.ECONOMIC_DEVELOPMENT]: 'Economic Development'
};

export const EVENT_TYPES = {
  WORKSHOP: 'workshop',
  CONFERENCE: 'conference',
  WEBINAR: 'webinar',
  HACKATHON: 'hackathon',
  NETWORKING: 'networking',
  TRAINING: 'training',
  SHOWCASE: 'showcase',
  MEETING: 'meeting'
};

export const EVENT_TYPE_LABELS = {
  [EVENT_TYPES?.WORKSHOP]: 'Workshop',
  [EVENT_TYPES?.CONFERENCE]: 'Conference',
  [EVENT_TYPES?.WEBINAR]: 'Webinar',
  [EVENT_TYPES?.HACKATHON]: 'Hackathon',
  [EVENT_TYPES?.NETWORKING]: 'Networking',
  [EVENT_TYPES?.TRAINING]: 'Training',
  [EVENT_TYPES?.SHOWCASE]: 'Showcase',
  [EVENT_TYPES?.MEETING]: 'Meeting'
};

export const GRANT_STATUSES = {
  OPEN: 'open',
  CLOSING_SOON: 'closing_soon',
  CLOSED: 'closed',
  REVIEW: 'review',
  AWARDED: 'awarded'
};

export const GRANT_STATUS_LABELS = {
  [GRANT_STATUSES?.OPEN]: 'Open',
  [GRANT_STATUSES?.CLOSING_SOON]: 'Closing Soon',
  [GRANT_STATUSES?.CLOSED]: 'Closed',
  [GRANT_STATUSES?.REVIEW]: 'Under Review',
  [GRANT_STATUSES?.AWARDED]: 'Awarded'
};

export const COURSE_LEVELS = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
  EXPERT: 'expert'
};

export const COURSE_LEVEL_LABELS = {
  [COURSE_LEVELS?.BEGINNER]: 'Beginner',
  [COURSE_LEVELS?.INTERMEDIATE]: 'Intermediate',
  [COURSE_LEVELS?.ADVANCED]: 'Advanced',
  [COURSE_LEVELS?.EXPERT]: 'Expert'
};

export const PRODUCT_STATUSES = {
  PROTOTYPE: 'prototype',
  PILOT: 'pilot',
  DEPLOYED: 'deployed',
  SCALED: 'scaled'
};

export const PRODUCT_STATUS_LABELS = {
  [PRODUCT_STATUSES?.PROTOTYPE]: 'Prototype',
  [PRODUCT_STATUSES?.PILOT]: 'Pilot',
  [PRODUCT_STATUSES?.DEPLOYED]: 'Deployed',
  [PRODUCT_STATUSES?.SCALED]: 'Scaled'
};

export const FORUM_POST_TYPES = {
  DISCUSSION: 'discussion',
  QUESTION: 'question',
  ANNOUNCEMENT: 'announcement',
  RESOURCE_SHARING: 'resource_sharing',
  COLLABORATION: 'collaboration'
};

export const FORUM_POST_TYPE_LABELS = {
  [FORUM_POST_TYPES?.DISCUSSION]: 'Discussion',
  [FORUM_POST_TYPES?.QUESTION]: 'Question',
  [FORUM_POST_TYPES?.ANNOUNCEMENT]: 'Announcement',
  [FORUM_POST_TYPES?.RESOURCE_SHARING]: 'Resource Sharing',
  [FORUM_POST_TYPES?.COLLABORATION]: 'Collaboration'
};

// ===================================================================
// HELPER FUNCTIONS
// ===================================================================

export const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString)?.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString)?.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatCurrency = (amount, currency = 'USD') => {
  if (!amount) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  })?.format(amount);
};

export const getDaysUntilDeadline = (deadline) => {
  if (!deadline) return null;
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diffTime = deadlineDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const isUpcoming = (dateString) => {
  if (!dateString) return false;
  return new Date(dateString) > new Date();
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text?.length <= maxLength) return text;
  return text?.substring(0, maxLength) + '...';
};

export const getInitials = (name) => {
  if (!name) return '';
  return name?.split(' ')?.map(word => word?.charAt(0)?.toUpperCase())?.slice(0, 2)?.join('');
};

export const generateUniqueId = () => {
  return Date.now()?.toString(36) + Math.random()?.toString(36)?.substr(2);
};

// ===================================================================
// VALIDATION HELPERS
// ===================================================================

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex?.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex?.test(phone?.replace(/\s/g, ''));
};

export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ===================================================================
// SEARCH AND FILTER HELPERS
// ===================================================================

export const filterByCategory = (items, category) => {
  if (!category || !items) return items;
  return items?.filter(item => item?.category === category);
};

export const filterByRole = (items, role) => {
  if (!role || !items) return items;
  return items?.filter(item => item?.role === role);
};

export const searchItems = (items, searchTerm, fields = ['name', 'title', 'description']) => {
  if (!searchTerm || !items) return items;
  const term = searchTerm?.toLowerCase();
  return items?.filter(item => 
    fields?.some(field => 
      item?.[field]?.toLowerCase()?.includes(term)
    )
  );
};

export const sortByDate = (items, field = 'created_at', ascending = false) => {
  if (!items) return [];
  return [...items]?.sort((a, b) => {
    const dateA = new Date(a?.[field]);
    const dateB = new Date(b?.[field]);
    return ascending ? dateA - dateB : dateB - dateA;
  });
};

export const sortByName = (items, field = 'name', ascending = true) => {
  if (!items) return [];
  return [...items]?.sort((a, b) => {
    const nameA = (a?.[field] || '')?.toLowerCase();
    const nameB = (b?.[field] || '')?.toLowerCase();
    if (ascending) {
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    } else {
      return nameB < nameA ? -1 : nameB > nameA ? 1 : 0;
    }
  });
};

// ===================================================================
// COLOR AND STYLING HELPERS
// ===================================================================

export const getRoleColor = (role) => {
  const colors = {
    [USER_ROLES?.CITY_OFFICIAL]: 'text-blue-600 bg-blue-100',
    [USER_ROLES?.STUDENT]: 'text-green-600 bg-green-100',
    [USER_ROLES?.PCF_ADMIN]: 'text-red-600 bg-red-100'
  };
  return colors?.[role] || 'text-gray-600 bg-gray-100';
};

export const getCategoryColor = (category) => {
  const colors = {
    [BUSINESS_CATEGORIES?.TECHNOLOGY]: 'text-blue-600 bg-blue-100',
    [BUSINESS_CATEGORIES?.TRANSPORTATION]: 'text-green-600 bg-green-100',
    [BUSINESS_CATEGORIES?.ENERGY]: 'text-yellow-600 bg-yellow-100',
    [BUSINESS_CATEGORIES?.WATER_MANAGEMENT]: 'text-cyan-600 bg-cyan-100',
    [BUSINESS_CATEGORIES?.WASTE_MANAGEMENT]: 'text-brown-600 bg-brown-100',
    [BUSINESS_CATEGORIES?.GOVERNANCE]: 'text-purple-600 bg-purple-100',
    [BUSINESS_CATEGORIES?.EDUCATION]: 'text-indigo-600 bg-indigo-100',
    [BUSINESS_CATEGORIES?.HEALTHCARE]: 'text-pink-600 bg-pink-100',
    [BUSINESS_CATEGORIES?.SAFETY_SECURITY]: 'text-red-600 bg-red-100',
    [BUSINESS_CATEGORIES?.ECONOMIC_DEVELOPMENT]: 'text-orange-600 bg-orange-100'
  };
  return colors?.[category] || 'text-gray-600 bg-gray-100';
};

export const getStatusColor = (status, type) => {
  if (type === 'grant') {
    const colors = {
      [GRANT_STATUSES?.OPEN]: 'text-green-600 bg-green-100',
      [GRANT_STATUSES?.CLOSING_SOON]: 'text-yellow-600 bg-yellow-100',
      [GRANT_STATUSES?.CLOSED]: 'text-red-600 bg-red-100',
      [GRANT_STATUSES?.REVIEW]: 'text-blue-600 bg-blue-100',
      [GRANT_STATUSES?.AWARDED]: 'text-purple-600 bg-purple-100'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-100';
  }
  
  if (type === 'product') {
    const colors = {
      [PRODUCT_STATUSES?.PROTOTYPE]: 'text-orange-600 bg-orange-100',
      [PRODUCT_STATUSES?.PILOT]: 'text-blue-600 bg-blue-100',
      [PRODUCT_STATUSES?.DEPLOYED]: 'text-green-600 bg-green-100',
      [PRODUCT_STATUSES?.SCALED]: 'text-purple-600 bg-purple-100'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-100';
  }
  
  return 'text-gray-600 bg-gray-100';
};

export const getEventTypeIcon = (eventType) => {
  const icons = {
    [EVENT_TYPES?.WORKSHOP]: 'Wrench',
    [EVENT_TYPES?.CONFERENCE]: 'Users',
    [EVENT_TYPES?.WEBINAR]: 'Monitor',
    [EVENT_TYPES?.HACKATHON]: 'Code',
    [EVENT_TYPES?.NETWORKING]: 'Network',
    [EVENT_TYPES?.TRAINING]: 'GraduationCap',
    [EVENT_TYPES?.SHOWCASE]: 'Star',
    [EVENT_TYPES?.MEETING]: 'Calendar'
  };
  return icons?.[eventType] || 'Calendar';
};

export const getCategoryIcon = (category) => {
  const icons = {
    [BUSINESS_CATEGORIES?.TECHNOLOGY]: 'Cpu',
    [BUSINESS_CATEGORIES?.TRANSPORTATION]: 'Car',
    [BUSINESS_CATEGORIES?.ENERGY]: 'Zap',
    [BUSINESS_CATEGORIES?.WATER_MANAGEMENT]: 'Droplets',
    [BUSINESS_CATEGORIES?.WASTE_MANAGEMENT]: 'Trash2',
    [BUSINESS_CATEGORIES?.GOVERNANCE]: 'Building2',
    [BUSINESS_CATEGORIES?.EDUCATION]: 'GraduationCap',
    [BUSINESS_CATEGORIES?.HEALTHCARE]: 'Heart',
    [BUSINESS_CATEGORIES?.SAFETY_SECURITY]: 'Shield',
    [BUSINESS_CATEGORIES?.ECONOMIC_DEVELOPMENT]: 'TrendingUp'
  };
  return icons?.[category] || 'Building2';
};

export default {
  USER_ROLES,
  USER_ROLE_LABELS,
  BUSINESS_CATEGORIES,
  BUSINESS_CATEGORY_LABELS,
  EVENT_TYPES,
  EVENT_TYPE_LABELS,
  GRANT_STATUSES,
  GRANT_STATUS_LABELS,
  COURSE_LEVELS,
  COURSE_LEVEL_LABELS,
  PRODUCT_STATUSES,
  PRODUCT_STATUS_LABELS,
  FORUM_POST_TYPES,
  FORUM_POST_TYPE_LABELS,
  formatDate,
  formatDateTime,
  formatCurrency,
  getDaysUntilDeadline,
  isUpcoming,
  truncateText,
  getInitials,
  generateUniqueId,
  validateEmail,
  validatePhone,
  validateUrl,
  filterByCategory,
  filterByRole,
  searchItems,
  sortByDate,
  sortByName,
  getRoleColor,
  getCategoryColor,
  getStatusColor,
  getEventTypeIcon,
  getCategoryIcon
};